import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { copyTextToClipboard, needsManualCopy } from 'utils/clipboard';
import { CircularProgress, Drawer } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MicOffRoundedIcon from '@mui/icons-material/MicOffRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

import { useAppDispatch, useAppSelector } from 'hooks/store';
import {
  CallScribeCallDirection,
  CallScribeCallStatus,
  CallScribeConsentResult,
  CallScribePipelineStage,
  CallScribeTimeEntryStatus,
  ICallScribeCallListItem,
  ISentimentJudgment,
} from 'services/callScribe/types';
import {
  getCallScribeEmpathicAnalyses,
  rerunCallScribeIntelligence,
} from 'services/callScribe';
import { extractApiErrorMessage } from 'services/callScribe/apiError';
import { isInFlightStage } from 'components/CallScribe/postCallReviewPhase';
import { UserDataSelector } from 'store/user';
import { isUserAdmin } from 'utils/isUserAdmin';
import { ThunkGetCallScribeCall } from 'store/callScribe/thunks';
import {
  CallScribeParticipantsForCallSelector,
  CallScribePipelineForCallSelector,
} from 'store/callScribe/selectors';

import CallRecordingPlayer from 'components/CallScribe/CallRecordingPlayer';
import { recordingStoppedLabel } from 'components/CallScribe/recordingStop';
import CallReviewPanel from 'components/CallScribe/CallReviewPanel';
import TimeEntrySuggestion from 'components/CallScribe/TimeEntrySuggestion';
import ConversationSection from 'components/CallScribe/ConversationSection';
import { noSummarySentence } from 'components/CallScribe/notRecorded';
import CallInsightsHeader from 'components/CallScribe/CallInsightsHeader';
import EmotionSpectrum from 'components/CallScribe/EmotionTimeline/EmotionSpectrum';
import { resolveDurationMs, formatClock } from 'components/CallScribe/EmotionTimeline/emotionSpectrumModel';
import { parseSentimentJudgment } from 'components/CallScribe/EmotionTimeline/emotionTimelineModel';
import {
  complianceFlagPhrases,
  IActionItem,
  parseActionItems,
} from 'components/CallScribe/reviewHelpers';
import CallChipRow, { parseTopics } from 'components/CallScribe/callChips';
import Avatar from 'components/CallScribe/ui/Avatar';
import Chip from 'components/CallScribe/ui/Chip';
import usePaperStyles from 'components/CallScribe/paperStyles';
import useCallDetailDrawerStyles from 'components/CallScribe/callDetailDrawerStyles';
import { startReconcilePoll } from 'components/CallScribe/reconcilePoll';
import {
  needsReconcile,
  RECONCILE_MAX_FETCHES,
  RECONCILE_MS,
} from 'components/CallScribe/drawerReconcile';
import {
  formatPhoneE164ToDisplay,
  otherPartyPhone,
  resolvePipelineRowState,
} from 'components/CallScribe/Recents/recentsHelpers';
import {
  CallScribeContactNoteStatus,
  contactNoteText,
  readContactNote,
} from 'components/CallScribe/contactNote';
import { noteOutcomeLabel } from 'components/CallScribe/noteOutcomes';
import {
  LINK_LABEL,
  formatDurationLabel,
  formatLocalDateTime,
} from 'components/CallScribe/callLogColumns';

const PIPELINE_STAGE_LABEL: Partial<Record<CallScribePipelineStage, string>> = {
  [CallScribePipelineStage.Received]: 'Recording received…',
  [CallScribePipelineStage.Transcribing]: 'Transcribing call…',
  [CallScribePipelineStage.Summarizing]: 'Generating summary…',
};

const IN_PROGRESS = new Set<CallScribeCallStatus>([
  CallScribeCallStatus.Initiated,
  CallScribeCallStatus.Ringing,
  CallScribeCallStatus.Answered,
]);

type TJumpId = 'summary' | 'emotion' | 'transcript' | 'ticket';

const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']);

const JUMPS: Array<{ id: TJumpId; label: string }> = [
  { id: 'summary', label: 'Summary' },
  { id: 'emotion', label: 'Emotion' },
  { id: 'transcript', label: 'Transcript' },
  { id: 'ticket', label: 'Ticket' },
];

export interface ICallDetailDrawerProps {
  call: ICallScribeCallListItem | null;
  onClose: () => void;
}

const CallDetailDrawer: React.FC<ICallDetailDrawerProps> = ({ call, onClose }) => {
  const classes = useCallDetailDrawerStyles();
  const paper = usePaperStyles();
  const dispatch = useAppDispatch();
  const [rerunBusy, setRerunBusy] = useState<boolean>(false);
  const [rerunQueued, setRerunQueued] = useState<boolean>(false);
  const rerunTimerRef = useRef<number | undefined>(undefined);
  const [rerunMessage, setRerunMessage] = useState<string | null>(null);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  // Holds the id when copying could not be PROVEN, so the technician can select it by hand rather
  // than being told it worked and discovering later that it did not.
  const [manualCopyValue, setManualCopyValue] = useState<string | null>(null);
  // The list item is the only source: the write-back pushes no socket event, and every status
  // transition happens before the contact note posts, so a socket field would be stale when sent.
  const contactNote = readContactNote(call?.contactNote);
  const customerNoteLabel = noteOutcomeLabel(call?.customerNoteOutcome);
  const internalNoteLabel = noteOutcomeLabel(call?.internalNoteOutcome);
  const pipelineEvent = useAppSelector(CallScribePipelineForCallSelector(call?.id));
  const callId = call?.id;
  const storeParticipants = useAppSelector(CallScribeParticipantsForCallSelector(callId));
  const participants = storeParticipants ?? call?.participants;
  const currentUser = useAppSelector(UserDataSelector);
  const canRename = Boolean(
    call &&
      (isUserAdmin(currentUser?.roleId) ||
        (typeof currentUser?.id === 'number' && call.initiatedByUserId === currentUser.id)),
  );
  const [judgment, setJudgment] = useState<ISentimentJudgment | null>(null);
  const [focusStartMs, setFocusStartMs] = useState<number | null>(null);
  const [seekMs, setSeekMs] = useState<number | null>(null);
  const [jumpAnnouncement, setJumpAnnouncement] = useState('');

  useEffect(() => {
    if (typeof callId !== 'number') {
      setJudgment(null);
      return undefined;
    }
    let cancelled = false;
    setFocusStartMs(null);
    setSeekMs(null);
    getCallScribeEmpathicAnalyses(callId)
      .then(res => {
        if (cancelled) return;
        const v2Row = (res?.data ?? []).find(e => e.judgmentJson);
        setJudgment(
          v2Row ? parseSentimentJudgment(v2Row.judgmentJson) : null,
        );
      })
      .catch(() => {
        if (!cancelled) setJudgment(null);
      });
    return () => {
      cancelled = true;
    };
  }, [callId]);

  const onSelectTurn = ({ startMs, recordingMs }: { startMs: number; recordingMs: number }): void => {
    setFocusStartMs(startMs);
    setSeekMs(recordingMs);
    setJumpAnnouncement(`Playing from ${formatClock(recordingMs)}`);
  };

  const reconcile = needsReconcile(call);
  const [reconciledOutFor, setReconciledOutFor] = useState<number | null>(null);

  useEffect(() => {
    if (typeof callId !== 'number' || !reconcile) return undefined;
    let fetched = 0;
    return startReconcilePoll(() => {
      if (fetched >= RECONCILE_MAX_FETCHES) {
        setReconciledOutFor(callId);
        return 'stop';
      }
      fetched += 1;
      void dispatch(ThunkGetCallScribeCall(callId));
      return undefined;
    }, RECONCILE_MS);
  }, [callId, reconcile, dispatch]);

  const actionItems = useMemo<IActionItem[]>(
    () => parseActionItems(call?.actionItems),
    [call?.actionItems],
  );

  const topics = useMemo<string[]>(
    () => parseTopics(call?.topics),
    [call?.topics],
  );

  const complianceFlags = useMemo<string[]>(
    () => complianceFlagPhrases(call?.complianceFlags),
    [call?.complianceFlags],
  );

  const noSummary = call && !call.hasIntelligence ? noSummarySentence(call) : null;
  const pipelineBody = call?.hasIntelligence
    ? 'Re-run to regenerate the summary and action items from the recording.'
    : noSummary !== null
      ? noSummary
      : resolvePipelineRowState(call?.pipelineStage) === 'nothingToSummarise'
      ? 'There was no conversation to summarise on this call, so the pipeline produced nothing.'
      : 'Intelligence has not been generated for this call yet. Re-run the pipeline if it failed.';

  useEffect(() => () => {
    if (rerunTimerRef.current !== undefined) {
      window.clearTimeout(rerunTimerRef.current);
      rerunTimerRef.current = undefined;
    }
    setRerunQueued(false);
    setRerunMessage(null);
  }, [callId]);

  const rerunInFlight = isInFlightStage(pipelineEvent?.stage ?? call?.pipelineStage);

  const onRerun = async (): Promise<void> => {
    if (!call) {
      return;
    }
    setRerunBusy(true);
    setRerunMessage(null);
    try {
      await rerunCallScribeIntelligence(call.id);
      setRerunQueued(true);
      setRerunMessage('Re-run queued. Refreshing in ~30s…');
      if (rerunTimerRef.current !== undefined) {
        window.clearTimeout(rerunTimerRef.current);
      }
      rerunTimerRef.current = window.setTimeout(() => {
        rerunTimerRef.current = undefined;
        void Promise.resolve(dispatch(ThunkGetCallScribeCall(call.id))).finally(() => {
          setRerunQueued(false);
          setRerunMessage('Refreshed.');
        });
      }, 30_000);
    } catch (e) {
      setRerunMessage(extractApiErrorMessage(e, 'Could not queue re-run. Try again.'));
    } finally {
      setRerunBusy(false);
    }
  };

  // The pod runs in a cross-origin PSA iframe, where the async Clipboard API is denied by the
  // embedder's Permissions Policy — so on Halo this always threw, the .catch() swallowed it, and
  // the button silently did nothing with no feedback at all. Uses the same helper ScheduleAssist
  // does, which falls back to execCommand and reports three outcomes rather than two.
  //
  // `unverified` deliberately does NOT claim success: execCommand reports that the command was
  // accepted, never that the clipboard took the value. Saying "Copied" there sends someone to
  // paste nothing and find out later. Both non-proven outcomes show the id for manual selection.
  const onCopyTicket = async (): Promise<void> => {
    const ticketId = call?.externalTicketId;
    if (!ticketId) {
      return;
    }

    const outcome = await copyTextToClipboard(ticketId);
    if (needsManualCopy(outcome)) {
      setManualCopyValue(ticketId);
      return;
    }

    setCopyFeedback('Copied');
  };

  const isOpen = call !== null;
  const otherParty = call ? otherPartyPhone(call) : null;
  const outbound = call?.direction === CallScribeCallDirection.Outbound;
  const ourNumber = outbound ? call?.fromNumber : call?.toNumber;
  const displayName = call?.contactName ?? (formatPhoneE164ToDisplay(otherParty) || '—');
  const callEnded = Boolean(call && (call.endedAtUtc || !IN_PROGRESS.has(call.status)));

  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const jumpRefs = useRef<Partial<Record<TJumpId, HTMLElement | null>>>({});
  const [activeJump, setActiveJump] = useState<TJumpId>('summary');
  const chosenJumpRef = useRef<TJumpId | null>(null);
  const jumps = JUMPS.filter(j => j.id !== 'emotion' || call?.hasIntelligence);
  const jumpKey = jumps.map(j => j.id).join(',');

  const jumpTarget = (id: TJumpId) => (node: HTMLElement | null): void => {
    jumpRefs.current[id] = node;
  };

  useEffect(() => {
    chosenJumpRef.current = null;
    setActiveJump('summary');
  }, [callId]);

  useEffect(() => {
    const root = scrollEl;
    if (!root || typeof IntersectionObserver === 'undefined') return undefined;
    const tops = new Map<string, number>();
    const pick = (): void => {
      if (chosenJumpRef.current) {
        setActiveJump(chosenJumpRef.current);
        return;
      }
      const last = jumps[jumps.length - 1];
      if (last && root.scrollTop > 0 && root.scrollTop + root.clientHeight >= root.scrollHeight - 2) {
        setActiveJump(last.id);
        return;
      }
      const nearest = [...tops.entries()]
        .filter(([, top]) => Number.isFinite(top))
        .sort((a, b) => a[1] - b[1])[0];
      if (nearest) setActiveJump(nearest[0] as TJumpId);
    };
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const id = entry.target.getAttribute('data-jump') ?? '';
        tops.set(id, entry.isIntersecting ? entry.boundingClientRect.top : Number.POSITIVE_INFINITY);
      });
      pick();
    }, { root, rootMargin: '-64px 0px -55% 0px', threshold: 0 });
    Object.values(jumpRefs.current).forEach(node => { if (node) observer.observe(node); });
    const onScroll = (): void => { pick(); };
    const release = (): void => { chosenJumpRef.current = null; };
    const onKey = (e: KeyboardEvent): void => { if (SCROLL_KEYS.has(e.key)) release(); };
    const onPointer = (e: PointerEvent): void => { if (e.target === root) release(); };
    root.addEventListener('scroll', onScroll, { passive: true });
    root.addEventListener('wheel', release, { passive: true });
    root.addEventListener('touchmove', release, { passive: true });
    root.addEventListener('keydown', onKey);
    root.addEventListener('pointerdown', onPointer);
    return () => {
      observer.disconnect();
      root.removeEventListener('scroll', onScroll);
      root.removeEventListener('wheel', release);
      root.removeEventListener('touchmove', release);
      root.removeEventListener('keydown', onKey);
      root.removeEventListener('pointerdown', onPointer);
    };
  }, [scrollEl, callId, jumpKey]);

  const jumpTo = useCallback((id: TJumpId): void => {
    const node = jumpRefs.current[id];
    if (!node) return;
    const reduce = typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    node.scrollIntoView?.({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    node.focus({ preventScroll: true });
    chosenJumpRef.current = id;
    setActiveJump(id);
  }, []);

  const recordingBlock = call ? (
    <>
      {recordingStoppedLabel(call) ? (
        <p className={paper.caption}>{recordingStoppedLabel(call)}</p>
      ) : null}
      {call.hasRecording && call.isCustomerRecordingOptIn ? (
        <CallRecordingPlayer
          callId={call.id}
          seekMs={seekMs}
          durationSeconds={call.recordingDurationSeconds ?? call.durationSeconds}
        />
      ) : (
        <div className={classes.noRecording}>
          <MicOffRoundedIcon aria-hidden />
          <span>
            {call.isCustomerRecordingOptIn
              ? 'No recording stored for this call.'
              : 'The customer opted out of recording, so this call was not recorded.'}
          </span>
        </div>
      )}
    </>
  ) : null;

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      aria-label="Call details"
      PaperProps={{
        className: classes.paper,
        sx: { width: { xs: '100%', sm: 460 } },
      }}
    >
      {call ? (
        <div className={classes.content} ref={setScrollEl}>
          <div className={classes.header}>
            <Avatar
              name={call.contactName ?? ''}
              tone={call.contactName ? 1 : null}
              size="lg"
              icon={<PhoneRoundedIcon />}
            />
            <div className={classes.headerBody}>
              <h2 className={classes.headerTitle} title={displayName}>
                {displayName}
              </h2>
              <p className={classes.headerMeta}>
                {call.contactName ? `${formatPhoneE164ToDisplay(otherParty) || '—'} · ` : ''}
                {outbound ? 'from' : 'to'} {formatPhoneE164ToDisplay(ourNumber) || '—'}
              </p>
              <p className={classes.headerMeta}>
                {formatLocalDateTime(call.startedAtUtc ?? call.createdAtUtc)}
                {' · '}
                {formatDurationLabel(call.durationSeconds)}
              </p>
              {call.consentResult === CallScribeConsentResult.Declined ? (
                <p className={classes.headerMeta}>Consent declined</p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close detail"
              className={`${paper.ghostButton} ${classes.closeButton}`}
            >
              <CloseRoundedIcon aria-hidden />
            </button>
          </div>

          <CallChipRow call={call} className={classes.chips} />

          <nav className={classes.jump} aria-label="Jump to a section">
            {jumps.map(j => (
              <button
                key={j.id}
                type="button"
                className={classes.jumpLink}
                aria-current={activeJump === j.id ? 'location' : undefined}
                onClick={() => { jumpTo(j.id); }}
              >
                {j.label}
              </button>
            ))}
          </nav>

          <div className={classes.sections}>
            {recordingBlock}

            <div ref={jumpTarget('summary')} data-jump="summary" tabIndex={-1} className={classes.jumpTarget}>
              {call.hasIntelligence ? (
                <CallInsightsHeader
                  call={call}
                  variant="section"
                  title="Summary"
                  footer={<CallReviewPanel call={call} />}
                >
                  {call.summaryShort ? (
                    <p className={paper.body}>{call.summaryShort}</p>
                  ) : null}
                </CallInsightsHeader>
              ) : (
                <section className={paper.section} aria-labelledby="cs-drawer-summary">
                  <div className={paper.sectionHead}>
                    <h3 id="cs-drawer-summary" className={paper.sectionLabel}>Summary</h3>
                  </div>
                  <div className={classes.inlineNote}>
                    <InfoOutlinedIcon aria-hidden />
                    <p className={paper.small}>{pipelineBody}</p>
                  </div>
                </section>
              )}
              {callEnded ? (
                <TimeEntrySuggestion call={call} reconcileExhausted={reconciledOutFor === call.id} />
              ) : null}
            </div>

            {call.hasIntelligence ? (
              <div ref={jumpTarget('emotion')} data-jump="emotion" tabIndex={-1} className={classes.jumpTarget}>
                <section className={paper.section} aria-labelledby="cs-drawer-emotion">
                  <div className={paper.sectionHead}>
                    <h3 id="cs-drawer-emotion" className={paper.sectionLabel}>Emotion across the call</h3>
                  </div>
                  <EmotionSpectrum
                    judgment={judgment}
                    durationMs={resolveDurationMs(call.recordingDurationSeconds, call.durationSeconds, judgment)}
                    gate={call.sentimentGate}
                    onSelectTurn={onSelectTurn}
                    participants={participants}
                  />
                  <div role="status" className={paper.srOnly}>
                    {jumpAnnouncement}
                  </div>
                </section>
              </div>
            ) : null}

            {actionItems.length > 0 ? (
              <section className={paper.section} aria-labelledby="cs-drawer-actions">
                <div className={paper.sectionHead}>
                  <h3 id="cs-drawer-actions" className={paper.sectionLabel}>Action items</h3>
                </div>
                <ul className={classes.actionItems}>
                  {actionItems.map((item, idx) => (
                    <li key={idx}>
                      {item.text}
                      {item.owner ? (
                        <span className={classes.actionItemMeta}> · {item.owner}</span>
                      ) : null}
                      {item.due ? (
                        <span className={classes.actionItemMeta}> · due {item.due}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {topics.length > 2 ? (
              <section className={paper.section} aria-labelledby="cs-drawer-topics">
                <div className={paper.sectionHead}>
                  <h3 id="cs-drawer-topics" className={paper.sectionLabel}>Topics</h3>
                </div>
                <div className={classes.tagRow}>
                  {topics.map((t, idx) => (
                    <Chip key={idx} tone="outline">{t}</Chip>
                  ))}
                </div>
              </section>
            ) : null}

            {complianceFlags.length > 0 ? (
              <section className={paper.section} aria-labelledby="cs-drawer-compliance">
                <div className={paper.sectionHead}>
                  <h3 id="cs-drawer-compliance" className={paper.sectionLabel}>Compliance</h3>
                </div>
                <div className={classes.tagRow}>
                  {complianceFlags.map((f, idx) => (
                    <Chip key={idx} tone="negative" icon={<ErrorOutlineRoundedIcon />}>{f}</Chip>
                  ))}
                </div>
              </section>
            ) : null}

            <div ref={jumpTarget('transcript')} data-jump="transcript" tabIndex={-1} className={classes.jumpTarget}>
              <section className={paper.section} aria-labelledby="cs-drawer-transcript">
                <div className={paper.sectionHead}>
                  <h3 id="cs-drawer-transcript" className={paper.sectionLabel}>Transcript</h3>
                </div>
                <ConversationSection
                  callId={call.id}
                  live={false}
                  consentResult={call.consentResult}
                  focusStartMs={focusStartMs}
                  participants={participants}
                  canRename={canRename}
                />
              </section>
            </div>

            <div ref={jumpTarget('ticket')} data-jump="ticket" tabIndex={-1} className={classes.jumpTarget}>
              {(call.externalTicketId ?? (call.linkStatus !== null ? '_' : null)) ? (
                <section className={paper.section} aria-labelledby="cs-drawer-ticket">
                  <div className={paper.sectionHead}>
                    <h3 id="cs-drawer-ticket" className={paper.sectionLabel}>Ticket &amp; time entry</h3>
                  </div>
                  {call.externalTicketId ? (
                    <div className={classes.inlineRow}>
                      <div className={classes.sectionBody}>
                        Ticket <strong className={classes.ticketId}>{call.externalTicketId}</strong>
                      </div>
                      <button type="button" onClick={() => { void onCopyTicket(); }} className={paper.linkButton}>
                        <ContentCopyRoundedIcon aria-hidden />
                        Copy id
                      </button>
                    </div>
                  ) : null}
                  {manualCopyValue !== null ? (
                    <div className={classes.sectionBody} role="status">
                      Select and copy:{' '}
                      <input
                        readOnly
                        value={manualCopyValue}
                        onFocus={e => { e.currentTarget.select(); }}
                        aria-label="Ticket id, select to copy"
                        className={`${classes.ticketId} ${classes.copyField}`}
                      />
                    </div>
                  ) : null}
                  {copyFeedback !== null ? (
                    <div className={classes.sectionBody} role="status">{copyFeedback}</div>
                  ) : null}
                  {call.linkStatus !== null ? (
                    <div className={classes.sectionBody}>
                      Time entry: <strong>{LINK_LABEL[call.linkStatus]}</strong>
                      {call.linkPostedAtUtc
                        ? ` · ${formatLocalDateTime(call.linkPostedAtUtc)}`
                        : ''}
                    </div>
                  ) : null}
                  {call.linkErrorMessage && call.linkStatus !== CallScribeTimeEntryStatus.Failed ? (
                    <div className={classes.errorText}>{call.linkErrorMessage}</div>
                  ) : null}
                  {customerNoteLabel !== '' ? (
                    <div className={classes.sectionBody}>
                      Customer note: <strong>{customerNoteLabel}</strong>
                    </div>
                  ) : null}
                  {internalNoteLabel !== '' ? (
                    <div className={classes.sectionBody}>
                      Internal note: <strong>{internalNoteLabel}</strong>
                    </div>
                  ) : null}
                  {contactNote !== null ? (
                    <div className={classes.sectionBody} role="status">
                      Contact note:{' '}
                      <strong
                        className={
                          contactNote.status === CallScribeContactNoteStatus.Failed
                            ? classes.errorText
                            : undefined
                        }
                      >
                        {contactNoteText(contactNote)}
                      </strong>
                    </div>
                  ) : null}
                </section>
              ) : null}
            </div>

            <section className={paper.section} aria-labelledby="cs-drawer-pipeline">
              <div className={paper.sectionHead}>
                <h3 id="cs-drawer-pipeline" className={paper.sectionLabel}>Intelligence pipeline</h3>
              </div>
              {call.hasIntelligence ? (
                <p className={paper.small}>{pipelineBody}</p>
              ) : null}
              {pipelineEvent && pipelineEvent.stage < CallScribePipelineStage.Completed ? (
                <div className={classes.inlineRow}>
                  <CircularProgress size={12} thickness={5} className={classes.pipelineSpinner} />
                  <span className={paper.caption}>
                    {PIPELINE_STAGE_LABEL[pipelineEvent.stage] ?? 'Working…'}
                  </span>
                </div>
              ) : null}
              {pipelineEvent && pipelineEvent.stage === CallScribePipelineStage.Failed ? (
                <div className={classes.errorText}>
                  {pipelineEvent.error ?? 'The intelligence pipeline reported an error.'}
                </div>
              ) : null}
              <div className={classes.inlineRow}>
                <button
                  type="button"
                  className={paper.textButton}
                  disabled={rerunBusy || rerunQueued || rerunInFlight || !call.hasRecording}
                  onClick={() => { void onRerun(); }}
                >
                  <RefreshRoundedIcon aria-hidden />
                  {rerunBusy || rerunQueued || rerunInFlight ? 'Processing…' : 'Re-run intelligence'}
                </button>
                {rerunMessage ? (
                  <span className={paper.caption}>{rerunMessage}</span>
                ) : null}
              </div>
            </section>
          </div>
        </div>
      ) : null}
    </Drawer>
  );
};

export default CallDetailDrawer;
